import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import {
  CLIENT_LOGIN_URL,
  servicesMenuColumns,
  resourcesMenuColumns,
  resourcesFeatured
} from '../navigation/navigationConfig';
import './Navigation.css';

const BOOK_DEMO_URL = 'https://portal.versalaunch.com/book-demo';

function Navigation() {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAtTop, setIsAtTop] = useState(true);
  const navRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isServicesActive = location.pathname.startsWith('/services');
  const isResourcesActive = location.pathname.startsWith('/resources');
  const isHomeRoute = location.pathname === '/';

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 960) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setOpenDropdown(null);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the full-screen mobile drawer is open.
  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const updateScrollState = () => {
      const scrollY = window.scrollY;
      setIsAtTop((prev) => (prev ? scrollY <= 40 : scrollY <= 16));
    };

    updateScrollState();
    window.addEventListener('scroll', updateScrollState, { passive: true });
    return () => window.removeEventListener('scroll', updateScrollState);
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenDropdown(null);
        setMobileMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpenDropdown(null);
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleNavSelection = () => {
    setOpenDropdown(null);
    setMobileMenuOpen(false);
  };

  const openMenu = (menuName) => {
    if (window.innerWidth > 960) {
      setOpenDropdown(menuName);
    }
  };

  const closeMenu = () => {
    if (window.innerWidth > 960) {
      setOpenDropdown(null);
    }
  };

  const handleServicesClick = () => {
    if (window.innerWidth <= 960) {
      setOpenDropdown((prev) => (prev === 'services' ? null : 'services'));
      return;
    }

    handleNavSelection();
    navigate('/services');
  };

  const handleResourcesClick = () => {
    if (window.innerWidth <= 960) {
      setOpenDropdown((prev) => (prev === 'resources' ? null : 'resources'));
      return;
    }

    handleNavSelection();
    navigate('/resources');
  };

  const renderMenuLink = (item) => {
    if (item.external) {
      return (
        <a href={item.href} target="_blank" rel="noreferrer" onClick={handleNavSelection}>
          {item.label}
        </a>
      );
    }

    return (
      <Link to={item.to} onClick={handleNavSelection}>
        {item.label}
      </Link>
    );
  };

  return (
    <header className={`navbar navWrap navGlass ${isHomeRoute && isAtTop ? 'navbar-at-top' : ''}`} ref={navRef}>
      <div className="nav-container">
        <Link to="/" className="logo" onClick={handleNavSelection}>
          <img
            src="/versa-launch-logo.png"
            alt="Versa Launch"
            className="logo-image"
          />
        </Link>

        <div className="mobile-header-actions">
          <a
            href={CLIENT_LOGIN_URL}
            className="login-btn"
            target="_blank"
            rel="noreferrer"
            onClick={handleNavSelection}
          >
            Login
          </a>
          <Link to="/plans" className="cta-btn" onClick={handleNavSelection}>
            Get started
          </Link>
        </div>

        <button
          type="button"
          className="mobile-menu-toggle"
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileMenuOpen}
          onClick={() => {
            setMobileMenuOpen((prev) => !prev);
            setOpenDropdown(null);
          }}
        >
          {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>

        <div className={`nav-main ${mobileMenuOpen ? 'open' : ''}`}>
          <nav className="nav-links">
            <div
              className={`nav-item dropdown ${openDropdown === 'services' ? 'is-open' : ''}`}
              onMouseEnter={() => openMenu('services')}
              onMouseLeave={closeMenu}
            >
              <button
                type="button"
                className={`nav-link nav-text-link ${isServicesActive ? 'is-active' : ''}`}
                onClick={handleServicesClick}
              >
                Services
                <ChevronDown size={15} className={`nav-chevron ${openDropdown === 'services' ? 'open' : ''}`} aria-hidden="true" />
              </button>
              <div className="dropdown-menu" id="services-dropdown-menu">
                {servicesMenuColumns.map((column) => (
                  <div className="dropdown-column" key={column.title}>
                    <span className="dropdown-title">{column.title}</span>
                    {column.links.map((item) => (
                      <React.Fragment key={item.label}>{renderMenuLink(item)}</React.Fragment>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            <NavLink
              to="/plans"
              onClick={handleNavSelection}
              className={({ isActive }) => `nav-text-link ${isActive ? 'is-active' : ''}`}
            >
              Pricing
            </NavLink>
            <NavLink
              to="/resources/getting-started"
              onClick={handleNavSelection}
              className={({ isActive }) => `nav-text-link ${isActive ? 'is-active' : ''}`}
            >
              How it works
            </NavLink>
            <div
              className={`nav-item dropdown ${openDropdown === 'resources' ? 'is-open' : ''}`}
              onMouseEnter={() => openMenu('resources')}
              onMouseLeave={closeMenu}
            >
              <button
                type="button"
                className={`nav-link nav-text-link ${isResourcesActive ? 'is-active' : ''}`}
                onClick={handleResourcesClick}
              >
                Resources
                <ChevronDown size={15} className={`nav-chevron ${openDropdown === 'resources' ? 'open' : ''}`} aria-hidden="true" />
              </button>
              <div className="dropdown-menu resources-dropdown" id="resources-dropdown-menu">
                {resourcesMenuColumns.map((column) => (
                  <div className="dropdown-column" key={column.title}>
                    <span className="dropdown-title">{column.title}</span>
                    {column.links.map((item) => (
                      <React.Fragment key={item.label}>{renderMenuLink(item)}</React.Fragment>
                    ))}
                  </div>
                ))}
                <div className="dropdown-column dropdown-image-col">
                  <img src={resourcesFeatured.imageSrc} alt={resourcesFeatured.imageAlt} className="dropdown-featured-image" />
                  <div className="resource-image-content">
                    <span className="resource-kicker">{resourcesFeatured.kicker}</span>
                    <p>{resourcesFeatured.description}</p>
                  </div>
                  <Link to={resourcesFeatured.ctaTo} className="explore-resources-btn" onClick={handleNavSelection}>{resourcesFeatured.ctaLabel}</Link>
                </div>
              </div>
            </div>
          </nav>

          <div className="nav-actions">
            <a
              href={CLIENT_LOGIN_URL}
              className="login-btn"
              target="_blank"
              rel="noreferrer"
              onClick={handleNavSelection}
            >
              Login
            </a>
            <a href={BOOK_DEMO_URL} className="cta-btn" onClick={handleNavSelection}>
              Get a free demo
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Navigation;
